// src/table/AppMovimientoReactTable.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import styled from "styled-components";
import { useFilters, useSortBy, usePagination } from "react-table";
import {
  getMovimientos,
  deleteMovimiento,
  putMovimiento,
  getMovimientoById, // hidratar visibles
} from "../helpers/rutaMovimientos";
import ModalFormMovimiento from "../components/ModalFormMovimiento";
import Table from "./TableContainer";
import "../css/tablecomandas.css";

const Styles = styled.div`
  .table-container { overflow: auto; height: 600px; }
  table { border: 1px solid black; border-spacing: 0; font-size: 13px; }
  th, td { padding: 0.5rem; border-bottom: 1px solid black; border-right: 1px solid black; }
  th { background-color: #778899; position: sticky; top: 0; z-index: 2; text-align: center; font-size: 12px; }
  td { background-color: white; }
  .pagination { padding: 0.5rem; background-color: #778899; font-size: 15px; font-weight: bold; }
`;

/* ---------- utils ---------- */
function toStartOfDay(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  return isNaN(d) ? null : d;
}
function toEndOfDay(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T23:59:59");
  return isNaN(d) ? null : d;
}
function safeGet(obj, path) {
  try { return path.split(".").reduce((o, k) => (o && o[k] != null ? o[k] : ""), obj) ?? ""; }
  catch { return ""; }
}
function norm(v) {
  return (v ?? "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}
const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);

const money = (n) =>
  Number(n || 0).toLocaleString("es-AR", { style: "currency", currency: "ARS" });

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// limpia "(2)" al final, por estética
const cleanImputaLabel = (s) =>
  String(s || "").replace(/\s*\(\s*\d+\s*\)\s*$/, "").trim();

/** Lee montos nuevos con fallback para registros viejos */
function calcMontos(row) {
  const tipo = String(row?.tipo || "").toLowerCase();
  const total = num(row?.monto);

  const a = num(row?.montoA); // blanco
  // si montoB no existe en registros viejos, lo inferimos
  const b = row?.montoB != null ? num(row?.montoB) : Math.max(0, total - a);

  const alic = row?.ivaAlicuota != null ? num(row.ivaAlicuota) : 21;
  const incluye = row?.aIncluyeIVA !== false;

  // neto/iva guardados, si existen los respetamos
  let netoA = row?.netoA != null ? num(row.netoA) : 0;
  let ivaA  = row?.ivaA  != null ? num(row.ivaA)  : 0;

  // si no vienen guardados, los calculamos para mostrar
  if ((netoA === 0 && ivaA === 0) && a > 0 && alic > 0) {
    if (incluye) {
      const net = a / (1 + alic / 100);
      netoA = Number(net.toFixed(2));
      ivaA  = Number((a - net).toFixed(2));
    } else {
      netoA = Number(a.toFixed(2));
      ivaA  = Number((a * (alic / 100)).toFixed(2));
    }
  }

  return {
    tipo,
    total,
    montoA: a,
    montoB: b,
    ivaAlicuota: alic,
    aIncluyeIVA: incluye,
    netoA,
    ivaA,
    conFactura: a > 0,
  };
}

/* =======================================================
   Componente
======================================================= */
export default function AppMovimientoReactTable() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [movimientoEdit, setMovimientoEdit] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // ---- Filtros controlados ----
  const [filtros, setFiltros] = useState({
    desde: "", hasta: "", tipo: "", medio: "",
    descripcion: "", gasto: "", caja: "",
    persona: "", empresa: "", cliente: "",
    camion: "", chofer: "",
    imputa: "", // ✅ NUEVO
    usuario: "", libre: "",
  });
  const setF = (k, v) => setFiltros(prev => ({ ...prev, [k]: v }));

  /* ---------- HIDRATACIÓN RÁPIDA (solo visibles) ---------- */
  const VISIBLE_TO_ENRICH = 60;
  const CONCURRENCY = 16;

  // ✅ incluye fondo + imputa
  const KEYS = ["fondo","caja","cuentaBancaria","gasto","comanda","camion","chofer","cliente","empresa","persona","imputa"];
  const needsEnrich = (m) => KEYS.some(k => m?.[k] && typeof m[k] === "string");

  const enrichFirstPage = useCallback(async (list) => {
    const target = (list || []).slice(0, VISIBLE_TO_ENRICH).filter(needsEnrich);
    if (target.length === 0) return;

    let idx = 0;
    const updated = [...list];

    const worker = async () => {
      while (idx < target.length) {
        const current = target[idx++];
        try {
          const resp = await getMovimientoById(current._id);
          const det = resp?.movimiento;
          if (det) {
            const i = updated.findIndex(x => x._id === current._id);
            if (i >= 0) {
              const merged = { ...updated[i] };
              KEYS.forEach(k => { if (det[k]) merged[k] = det[k]; });
              updated[i] = merged;
            }
          }
        } catch { /* noop */ }
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, target.length) }, () => worker())
    );
    setData(updated);
  }, []);

  /* ---------- Fetch ---------- */
  const fetchData = useCallback(async () => {
    const resp = await getMovimientos();
    if (!resp?.ok) {
      console.error(resp?.error || "Error al obtener movimientos");
      setData([]);
      setLoading(false);
      return;
    }
    const list = resp.movimientos || [];
    setData(list);         // pinta inmediato
    enrichFirstPage(list); // hidrata visibles
    setLoading(false);
  }, [enrichFirstPage]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ---------- Acciones ---------- */
  const handleClose = () => {
    setShowModal(false);
    setMovimientoEdit(null);
    fetchData();
  };

  const borrar = useCallback(async (mongoId) => {
    if (!window.confirm("¿Eliminar movimiento?")) return;
    await deleteMovimiento(mongoId);
    setData(prev => prev.filter(m => m._id !== mongoId));
    fetchData();
  }, [fetchData]);

  const editar = useCallback(async (id, values) => {
    await putMovimiento(id, values);
    setData(prev => prev.map(m => (m._id === id ? { ...m, ...values } : m)));
    fetchData();
  }, [fetchData]);

  /* ---------- Fondo/Destino (tolerante a fondo/caja) ---------- */
  const renderDestino = useCallback((row) => {
    const medio = (row?.medio || "").toString().toUpperCase().trim();

    // 1) cuentaBancaria explícita
    const cta =
      (row && row.cuentaBancaria && typeof row.cuentaBancaria === "object")
        ? row.cuentaBancaria
        : null;

    // 2) fondo nuevo o caja legacy
    const fondo =
      (row && row.fondo && typeof row.fondo === "object")
        ? row.fondo
        : (row && row.caja && typeof row.caja === "object" ? row.caja : null);

    if (medio === "EFECTIVO") {
      if (fondo) return fondo.descripcion || fondo.nombre || fondo.codcaj || "EFECTIVO";
      return "EFECTIVO (—)";
    }

    if (medio === "TRANSFERENCIA") {
      if (cta) return cta.alias || [cta.banco, cta.numero].filter(Boolean).join(" ") || "—";
      if (fondo && ((fondo.tipo || "").toString().toUpperCase().includes("BANCO"))) {
        return fondo.alias || [fondo.banco, fondo.numero].filter(Boolean).join(" ") || "—";
      }
      return "—";
    }

    return "—"; // CHEQUE / ICHEQ hoy sin detalle
  }, []);

  /* ---------- Filtro en memoria ---------- */
  const dataFiltrada = useMemo(() => {
    const dDesde = toStartOfDay(filtros.desde);
    const dHasta = toEndOfDay(filtros.hasta);

    return (data || []).filter(r => {
      const f = r?.fecha ? new Date(r.fecha) : null;
      if (dDesde && (!f || f < dDesde)) return false;
      if (dHasta && (!f || f > dHasta)) return false;

      if (filtros.tipo && !norm(r?.tipo).includes(norm(filtros.tipo))) return false;
      if (filtros.medio && !norm(r?.medio).includes(norm(filtros.medio))) return false;
      if (filtros.descripcion && !norm(r?.descripcion).includes(norm(filtros.descripcion))) return false;

      if (filtros.gasto) {
        const g = r?.gasto;
        const gastoTxt = isObj(g)
          ? (g.gasto || g.nombre || g.descripcion || g.categoria || g.rubro || "")
          : "";
        if (!norm(gastoTxt).includes(norm(filtros.gasto))) return false;
      }

      // ✅ Imputa
      if (filtros.imputa) {
        const i = r?.imputa;
        const imputaTxt = isObj(i) ? (i.imputa || i.nombre || "") : "";
        if (!norm(imputaTxt).includes(norm(filtros.imputa))) return false;
      }

      // Fondo/Caja
      if (filtros.caja) {
        const fondo = isObj(r?.fondo) ? r.fondo : (isObj(r?.caja) ? r.caja : null);
        const cajaTxt = fondo
          ? (fondo.descripcion || fondo.nombre || fondo.codcaj || fondo.alias || fondo.banco || fondo.numero || "")
          : (typeof r?.caja === "string" ? "EFECTIVO" : "");
        if (!norm(cajaTxt).includes(norm(filtros.caja))) return false;
      }

      if (filtros.persona) {
        const p = r?.persona;
        const personaTxt = isObj(p) ? (p.nombre || p.codper || "") : "";
        if (!norm(personaTxt).includes(norm(filtros.persona))) return false;
      }

      if (filtros.empresa) {
        const e = r?.empresa;
        const empresaTxt = isObj(e) ? (e.empresa || e.razonSocial || e.codemp || "") : "";
        if (!norm(empresaTxt).includes(norm(filtros.empresa))) return false;
      }

      if (filtros.cliente) {
        const c = r?.cliente;
        const clienteTxt = isObj(c) ? (c.razonsocial || c.nombreFantasia || c.codcli || "") : "";
        if (!norm(clienteTxt).includes(norm(filtros.cliente))) return false;
      }

      if (filtros.camion) {
        const cm = r?.camion;
        const camionTxt = isObj(cm) ? (cm.camion || cm.patente || "") : "";
        if (!norm(camionTxt).includes(norm(filtros.camion))) return false;
      }

      if (filtros.chofer) {
        const ch = r?.chofer;
        const choferTxt = isObj(ch) ? (ch.nombre || "") : "";
        if (!norm(choferTxt).includes(norm(filtros.chofer))) return false;
      }

      if (filtros.usuario && !norm(safeGet(r, "usuario.nombres")).includes(norm(filtros.usuario))) return false;
      if (filtros.libre && !norm(r?.libre).includes(norm(filtros.libre))) return false;

      return true;
    });
  }, [data, filtros]);

  const limpiarFiltros = () => setFiltros({
    desde: "", hasta: "", tipo: "", medio: "",
    descripcion: "", gasto: "", caja: "",
    persona: "", empresa: "", cliente: "",
    camion: "", chofer: "",
    imputa: "",
    usuario: "", libre: "",
  });

  const quickHoy = () => {
    const hoy = new Date();
    const y = hoy.getFullYear();
    const m = String(hoy.getMonth() + 1).padStart(2, "0");
    const d = String(hoy.getDate()).padStart(2, "0");
    const iso = `${y}-${m}-${d}`;
    setF("desde", iso); setF("hasta", iso);
  };

  const quickUltimos7 = () => {
    const hoy = new Date();
    const d7 = new Date(hoy); d7.setDate(hoy.getDate() - 6);
    const toISO = (dt) => `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}-${String(dt.getDate()).padStart(2,"0")}`;
    setF("desde", toISO(d7)); setF("hasta", toISO(hoy));
  };

  /* ---------- Columnas ---------- */
  const columns = useMemo(() => [
    // Editar
    {
      id: "editar",
      Header: "Edita",
      accessor: "_id",
      width: 50,
      Cell: ({ row }) => (
        <button
          className="action-btn action-btn--edit"
          title="Editar"
          onClick={() => { setMovimientoEdit(row.original); setShowModal(true); }}
        >
          <i className="fa fa-pencil" aria-hidden="true" />
        </button>
      ),
    },
    // Eliminar
    {
      id: "eliminar",
      Header: "🗑️",
      accessor: "_id",
      width: 50,
      Cell: ({ row }) => (
        <button
          className="action-btn action-btn--del"
          title="Eliminar"
          onClick={() => borrar(row.original._id)}
        >
          <i className="fa fa-trash-o" aria-hidden="true" />
        </button>
      ),
    },

    { Header: "Nro", accessor: "codmov", width: 60 },

    {
      Header: "Fecha",
      accessor: row => (row.fecha ? new Date(row.fecha) : null),
      id: "fecha",
      width: 120,
      Cell: ({ value }) =>
        value instanceof Date
          ? value.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" })
          : "",
    },

    { Header: "Tipo", accessor: "tipo", width: 80 },
    { Header: "Medio", accessor: "medio", width: 120 },

    // Monto Ingreso (Total)
    {
      Header: "Monto Ingreso",
      accessor: row => String(row.tipo||"").toLowerCase() === "ingreso" ? (num(row.monto)) : 0,
      id: "montoIngreso",
      width: 120,
      Cell: ({ value }) => (
        <div style={{ textAlign: "right" }}>
          {value ? money(value) : ""}
        </div>
      ),
      Footer: info => {
        const total = info.rows.reduce((sum, row) => sum + (row.values.montoIngreso || 0), 0);
        return (
          <div style={{ textAlign: "right", color: "green", fontWeight: "bold" }}>
            {money(total)}
          </div>
        );
      }
    },

    // Monto Egreso (Total)
    {
      Header: "Monto Egreso",
      accessor: row => String(row.tipo||"").toLowerCase() === "egreso" ? (num(row.monto)) : 0,
      id: "montoEgreso",
      width: 120,
      Cell: ({ value }) => (
        <div style={{ textAlign: "right" }}>
          {value ? money(value) : ""}
        </div>
      ),
      Footer: info => {
        const totalE = info.rows.reduce((sum, row) => sum + (row.values.montoEgreso || 0), 0);
        const totalI = info.rows.reduce((sum, row) => sum + (row.values.montoIngreso || 0), 0);
        const saldo = totalI - totalE;
        return (
          <div style={{ textAlign: "right" }}>
            <div style={{ color: "red", fontWeight: "bold" }}>
              {money(totalE)}
            </div>
            <div style={{ fontWeight: "bold", color: "blue" }}>
              SALDO: {money(saldo)}
            </div>
          </div>
        );
      }
    },

    // ✅ NUEVOS CAMPOS MONTO (solo egresos normalmente, pero tolerante)
    {
      Header: "Monto B (Negro)",
      accessor: (row) => {
        const c = calcMontos(row);
        return c.tipo === "egreso" ? c.montoB : 0;
      },
      id: "montoB",
      width: 120,
      Cell: ({ value }) => <div style={{ textAlign: "right" }}>{value ? money(value) : ""}</div>,
      Footer: info => {
        const t = info.rows.reduce((s, r) => s + (r.values.montoB || 0), 0);
        return <div style={{ textAlign: "right", fontWeight: "bold" }}>{money(t)}</div>;
      }
    },
    {
      Header: "Monto A (Blanco)",
      accessor: (row) => {
        const c = calcMontos(row);
        return c.tipo === "egreso" ? c.montoA : 0;
      },
      id: "montoA",
      width: 120,
      Cell: ({ value }) => <div style={{ textAlign: "right" }}>{value ? money(value) : ""}</div>,
      Footer: info => {
        const t = info.rows.reduce((s, r) => s + (r.values.montoA || 0), 0);
        return <div style={{ textAlign: "right", fontWeight: "bold" }}>{money(t)}</div>;
      }
    },
    {
      Header: "IVA %",
      accessor: (row) => {
        const c = calcMontos(row);
        return (c.tipo === "egreso" && c.montoA > 0) ? c.ivaAlicuota : "";
      },
      id: "ivaPct",
      width: 70,
      Cell: ({ value }) => <div style={{ textAlign: "center" }}>{value !== "" ? `${value}%` : ""}</div>,
    },
    {
      Header: "Neto A",
      accessor: (row) => {
        const c = calcMontos(row);
        return c.tipo === "egreso" ? c.netoA : 0;
      },
      id: "netoA",
      width: 120,
      Cell: ({ value }) => <div style={{ textAlign: "right" }}>{value ? money(value) : ""}</div>,
      Footer: info => {
        const t = info.rows.reduce((s, r) => s + (r.values.netoA || 0), 0);
        return <div style={{ textAlign: "right", fontWeight: "bold" }}>{money(t)}</div>;
      }
    },
    {
      Header: "IVA A",
      accessor: (row) => {
        const c = calcMontos(row);
        return c.tipo === "egreso" ? c.ivaA : 0;
      },
      id: "ivaA",
      width: 120,
      Cell: ({ value }) => <div style={{ textAlign: "right" }}>{value ? money(value) : ""}</div>,
      Footer: info => {
        const t = info.rows.reduce((s, r) => s + (r.values.ivaA || 0), 0);
        return <div style={{ textAlign: "right", fontWeight: "bold" }}>{money(t)}</div>;
      }
    },

    { Header: "Descripción", accessor: "descripcion", width: 230 },

    // Gasto (tolerante)
    {
      Header: "Gasto",
      accessor: (row) => {
        const g = row?.gasto;
        if (isObj(g)) return g.gasto || g.nombre || g.descripcion || g.categoria || g.rubro || "";
        return "";
      },
      width: 160
    },

    // ✅ Imputa (nuevo)
    {
      Header: "Imputa",
      accessor: (row) => {
        const i = row?.imputa;
        if (isObj(i)) return cleanImputaLabel(i.imputa || i.nombre || "");
        return "";
      },
      width: 160
    },

    // Fondo/Destino
    {
      Header: "Fondo / Destino",
      accessor: (row) => renderDestino(row),
      id: "destino",
      width: 220
    },

    // Camión
    {
      Header: "Camión",
      accessor: (row) => {
        const cm = row?.camion;
        if (isObj(cm)) return cm.camion || cm.patente || "";
        return "";
      },
      width: 120
    },

    // Chofer
    {
      Header: "Chofer",
      accessor: (row) => {
        const ch = row?.chofer;
        if (isObj(ch)) return ch.nombre || "";
        return "";
      },
      width: 150
    },

    // Persona
    {
      Header: "Persona",
      accessor: (row) => {
        const p = row?.persona;
        if (p && typeof p === "object") return p.nombre || p.codper || "";
        return "";
      },
      width: 160,
    },

    // Empresa
    {
      Header: "Empresa",
      accessor: (row) => {
        const e = row?.empresa;
        if (e && typeof e === "object") return e.empresa || e.razonSocial || e.codemp || "";
        return "";
      },
      width: 180,
    },

    // Cliente
    {
      Header: "Cliente",
      accessor: (row) => {
        const c = row?.cliente;
        if (c && typeof c === "object") return c.razonsocial || c.nombreFantasia || c.codcli || "";
        return "";
      },
      width: 200,
    },

    { Header: "Libre", accessor: "libre", width: 300 },
    { Header: "Usuario", accessor: row => row.usuario?.nombres || "", width: 120 },
  ], [borrar, renderDestino]);

  return (
    <div className="container mt-4">
      <h3 className="text-center mb-2">LISTADO DE MOVIMIENTOS</h3>

     
      {/* Filtros */}
      <div className="container mb-2">
        {/* Row 1 (12 columnas exactas) */}
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-6 col-lg-2">
            <label className="form-label" style={{ fontSize: "1.5rem" }}>Fecha desde</label>
            <input
              type="date"
              className="form-control"
              value={filtros.desde}
              onChange={(e) => setF("desde", e.target.value)}
            />
          </div>

          <div className="col-12 col-md-6 col-lg-2">
            <label className="form-label" style={{ fontSize: "1.5rem" }}>Fecha hasta</label>
            <input
              type="date"
              className="form-control"
              value={filtros.hasta}
              onChange={(e) => setF("hasta", e.target.value)}
            />
          </div>

          <div className="col-12 col-md-6 col-lg-1">
            <label className="form-label" style={{ fontSize: "1.5rem" }}>Tipo</label>
            <input
              className="form-control"
              placeholder="ingreso / egreso"
              value={filtros.tipo}
              onChange={(e) => setF("tipo", e.target.value)}
            />
          </div>

          <div className="col-12 col-md-6 col-lg-1">
            <label className="form-label" style={{ fontSize: "1.5rem" }}>Medio</label>
            <input
              className="form-control"
              placeholder="efectivo / transf"
              value={filtros.medio}
              onChange={(e) => setF("medio", e.target.value)}
            />
          </div>

          <div className="col-12 col-md-6 col-lg-2">
            <label className="form-label" style={{ fontSize: "1.5rem" }}>Gasto</label>
            <input
              className="form-control"
              value={filtros.gasto}
              onChange={(e) => setF("gasto", e.target.value)}
            />
          </div>

          <div className="col-12 col-md-6 col-lg-2">
            <label className="form-label" style={{ fontSize: "1.5rem" }}>Imputa</label>
            <input
              className="form-control"
              value={filtros.imputa}
              onChange={(e) => setF("imputa", e.target.value)}
            />
          </div>

          <div className="col-12 col-md-6 col-lg-2">
            <label className="form-label" style={{ fontSize: "1.5rem" }}>Fondo</label>
            <input
              className="form-control"
              value={filtros.caja}
              onChange={(e) => setF("caja", e.target.value)}
            />
          </div>
        </div>

        {/* Row 2 (12 columnas exactas) */}
        <div className="row g-2 align-items-end mt-2">
          <div className="col-12 col-md-6 col-lg-2">
            <label className="form-label" style={{ fontSize: "1.5rem" }}>Persona</label>
            <input
              className="form-control"
              value={filtros.persona}
              onChange={(e) => setF("persona", e.target.value)}
            />
          </div>

          <div className="col-12 col-md-6 col-lg-2">
            <label className="form-label" style={{ fontSize: "1.5rem" }}>Empresa</label>
            <input
              className="form-control"
              value={filtros.empresa}
              onChange={(e) => setF("empresa", e.target.value)}
            />
          </div>

          <div className="col-12 col-md-6 col-lg-2">
            <label className="form-label" style={{ fontSize: "1.5rem" }}>Cliente</label>
            <input
              className="form-control"
              value={filtros.cliente}
              onChange={(e) => setF("cliente", e.target.value)}
            />
          </div>

          <div className="col-12 col-md-6 col-lg-2">
            <label className="form-label" style={{ fontSize: "1.5rem" }}>Camión</label>
            <input
              className="form-control"
              value={filtros.camion}
              onChange={(e) => setF("camion", e.target.value)}
            />
          </div>

          <div className="col-12 col-md-6 col-lg-2">
            <label className="form-label" style={{ fontSize: "1.5rem" }}>Chofer</label>
            <input
              className="form-control"
              value={filtros.chofer}
              onChange={(e) => setF("chofer", e.target.value)}
            />
          </div>

          <div className="col-12 col-md-6 col-lg-2 d-flex gap-2">
            <button className="btn btn-outline-secondary btn-sm flex-fill" onClick={quickHoy}
              style={{ fontSize: 16, lineHeight: 1.1, padding: "8px 12px" }}>
              Hoy
            </button>
            <button className="btn btn-outline-success btn-sm flex-fill" onClick={quickUltimos7}
              style={{ fontSize: 16, lineHeight: 1.1, padding: "8px 12px" }}>
              7 días
            </button>
            <button className="btn btn-outline-primary btn-sm flex-fill" onClick={limpiarFiltros}
              style={{ fontSize: 16, lineHeight: 1.1, padding: "8px 12px" }}>
              Limpiar
            </button>
          </div>
        </div>
      </div>


      <Styles>
        <div className="table-container">
          <Table
            columns={columns}
            data={dataFiltrada}
            loading={loading}
            plugins={[useFilters, useSortBy, usePagination]}
          />
        </div>
      </Styles>

      {movimientoEdit && (
        <ModalFormMovimiento
          show={showModal}
          handleClose={handleClose}
          movimiento={movimientoEdit}
          onSave={editar}
        />
      )}
    </div>
  );
}
